import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, MoveHorizontal, Maximize, Minimize, Info } from 'lucide-react';
import Button from './Button';

const MIN_W = 380;
const DEFAULT_W = 480;
const TOPBAR_H = 64;
const STATUSBAR_H = 32;

/**
 * Drawer latéral standardisé pour tous les formulaires de l'application.
 *
 * - Ouvre en **poussant** le contenu de la page (le contenu recule).
 * - Redimensionnable par étirement vers la gauche (poignée sur le bord gauche).
 * - Bouton plein écran / réduction : en plein écran le drawer s'arrête au niveau du sidebar.
 * - Largeurs min / max contraintes, comportement responsive (plein écran sur mobile).
 *
 * Exemple :
 *   <FormDrawer open={open} onClose={close} title="Ajout d'un utilisateur"
 *               hint="Renseignez les informations du nouvel utilisateur.">
 *       <FormUser />
 *   </FormDrawer>
 */
const FormDrawer = (props: {
    open: boolean;
    onClose: () => void;
    title: ReactNode;
    children: ReactNode;
    hint?: ReactNode;
    footer?: ReactNode;
    width?: number;
}) => {
    const { open, onClose } = props;

    // Largeur actuelle du drawer
    const [width, setWidth] = useState(props.width || DEFAULT_W);
    // Mode plein écran (s'arrête au sidebar)
    const [isFull, setIsFull] = useState(false);
    // Largeur sauvegardée avant plein écran
    const savedWidthRef = useRef<number>(props.width || DEFAULT_W);
    // État de drag
    const draggingRef = useRef(false);

    // Largeur du sidebar (lue depuis le DOM)
    const [sidebarW, setSidebarW] = useState(260);
    // Statusbar masquée < 960px → le drawer descend jusqu'en bas du viewport
    const [statusH, setStatusH] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 960) ? 0 : STATUSBAR_H);

    // Détecte la largeur du sidebar, le mode mobile et la visibilité de la statusbar
    useEffect(() => {
        const update = () => {
            const sb = document.querySelector('.eb-sidebar') as HTMLElement | null;
            if (sb) setSidebarW(sb.offsetWidth);
            setStatusH(window.innerWidth < 960 ? 0 : STATUSBAR_H);
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    // Largeur max = viewport - sidebar (s'arrête au sidebar)
    const maxW = isMobile ? window.innerWidth : Math.max(MIN_W, window.innerWidth - sidebarW);

    // Quand le drawer s'ouvre, réinitialiser la largeur
    useEffect(() => {
        if (open) {
            setWidth(props.width || DEFAULT_W);
            setIsFull(false);
        }
    }, [open, props.width]);

    // Pousser le contenu : ajoute padding-right au .eb-content + met à jour la variable CSS
    // IMPORTANT : ne toucher aux variables globales QUE quand le drawer est ouvert.
    // Sinon, plusieurs instances fermées (ex: UpdateUser dans chaque ligne du DataGrid)
    // remonteraient --eb-drawer-w à 0px et fermeraient le drawer actuellement ouvert.
    useEffect(() => {
        if (!open) return;

        const content = document.querySelector('.eb-content') as HTMLElement | null;
        const effectiveW = isMobile ? window.innerWidth : (isFull ? maxW : width);

        document.documentElement.style.setProperty('--eb-drawer-w', `${effectiveW}px`);
        if (content && !isMobile) {
            content.style.paddingRight = `${effectiveW + 8}px`;
            content.style.transition = 'padding-right .25s ease';
        }

        return () => {
            document.documentElement.style.setProperty('--eb-drawer-w', '0px');
            if (content) content.style.paddingRight = '';
        };
    }, [open, width, isFull, isMobile, maxW]);

    // Gestion du redimensionnement par drag
    const onDragStart = useCallback((e: React.MouseEvent) => {
        if (isFull || isMobile) return;
        e.preventDefault();
        draggingRef.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const startX = e.clientX;
        const startW = width;

        const onMove = (ev: MouseEvent) => {
            if (!draggingRef.current) return;
            const delta = startX - ev.clientX;
            const newW = Math.min(maxW, Math.max(MIN_W, startW + delta));
            setWidth(newW);
        };

        const onUp = () => {
            draggingRef.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [width, maxW, isFull, isMobile]);

    // Fermeture avec Échap (uniquement quand le drawer est ouvert)
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    if (typeof document === 'undefined') return null;

    return createPortal(
        <>
            {/* Overlay sombre sur le contenu (mobile uniquement) */}
            {open && isMobile && (
                <div className="eb-drawer-overlay" onClick={onClose} />
            )}
            <aside
                className={`eb-formdrawer${open ? ' open' : ''}${isFull ? ' full' : ''}${isMobile ? ' mobile' : ''}`}
                style={{
                    top: TOPBAR_H,
                    bottom: statusH,
                    right: 0,
                }}
                aria-hidden={!open}
            >
                {/* Poignée de redimensionnement (bord gauche) */}
                {!isMobile && !isFull && (
                    <div
                        className="eb-formdrawer-resize"
                        onMouseDown={onDragStart}
                        title="Étirer"
                    >
                        <MoveHorizontal size={18} />
                    </div>
                )}

                <div className="eb-formdrawer-inner">
                    {/* En-tête */}
                    <header className="eb-formdrawer-header">
                        <h2 className="eb-formdrawer-title">{props.title}</h2>
                        <div className="eb-formdrawer-actions">
                            {!isMobile && (
                                <button
                                    className="eb-formdrawer-iconbtn"
                                    onClick={() => {
                                        if (isFull) {
                                            setIsFull(false);
                                            setWidth(savedWidthRef.current);
                                        } else {
                                            savedWidthRef.current = width;
                                            setIsFull(true);
                                        }
                                    }}
                                    title={isFull ? 'Réduire' : 'Plein écran'}
                                >
                                    {isFull ? <Minimize size={18} /> : <Maximize size={18} />}
                                </button>
                            )}
                            <button
                                className="eb-formdrawer-iconbtn"
                                onClick={onClose}
                                title="Fermer"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </header>

                    {/* Indice contextuel */}
                    {props.hint && (
                        <div className="eb-formdrawer-hint">
                            <Info size={18} style={{ marginTop: 1, flexShrink: 0 }} />
                            <span>{props.hint}</span>
                        </div>
                    )}

                    {/* Corps scrollable — children rendus seulement si open */}
                    <div className="eb-formdrawer-body">
                        {open && props.children}
                    </div>

                    {/* Pied / actions — wrapper obligatoire même pour un footer custom */}
                    <footer className="eb-formdrawer-footer">
                        {props.footer !== undefined ? props.footer : (
                            <Button variant="outline" onClick={onClose}>Annuler</Button>
                        )}
                    </footer>
                </div>
            </aside>
        </>,
        document.body
    );
};

export default FormDrawer;
